import React from 'react'
import { RealEstateCategory, RealEstateFeatures, SegmentationType, TransactionType } from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_APARTMENT_SELL_FLOW_PATH } from '../../../../../services/routes/routes'
import { agentApartmentSellSchema } from './agentApartmentSellSchema'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONSTRUCTION,
  SCREEN_CONTACT_INFO,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Construction from '../../../../../components/pages/insertionPage/screens/construction/Construction'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import {
  bathroomField,
  estateFeatures,
  floorAndTotalFloor,
  interiorQuality,
  moveInDate,
  noBedrooms,
  parkingSpace,
  rentalIncome,
  rentSubsidy,
  usableAreaField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  courtageGroup,
  livingSpaceField,
  priceField,
  purchasePriceOnRequestToggle,
  roomField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { priceMaximizer } from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoSectionsWrapper'
import { PriceFieldType } from '../../../../../components/pages/insertionPage/screens/basicInfo/fields/priceField/PriceField'
import { buildingTypeField } from '../../../../../components/pages/insertionPage/screens/commonFields/buildingType/BuildingType'
import { priceToApiSell } from '../../../../../api/realEstate/converters/toApi/estate/price/priceToApi'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'

const agentApartmentSellFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} isInternationalAddressOption={true} isPriceOverviewLinkDisplayed={true} />,
  },
  {
    screen: SCREENS[SCREEN_OBJECT_NUMBERS],
    element: (props) => <ObjectNumbersScreen {...props} />,
  },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.apartmentOrHouseSell'
        properties={[
          livingSpaceField('leftOnAll'),
          roomField(),
          priceField(PriceFieldType.BUY_PRICE, getDefaultClasses('leftOnAll')),
          purchasePriceOnRequestToggle(),
          courtageGroup(),
          priceMaximizer(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allSell'
        properties={[
          estateFeatures([
            RealEstateFeatures.EQUIPPED_KITCHEN,
            RealEstateFeatures.ELEVATOR,
            RealEstateFeatures.BALCONY_TERRACE,
            RealEstateFeatures.GUEST_TOILET,
            RealEstateFeatures.SHARED_GARDEN,
            RealEstateFeatures.BASEMENT,
            RealEstateFeatures.STEPLESS_ACCESSIBLE,
            RealEstateFeatures.SUMMER_RESIDENCE_PRACTICAL,
            RealEstateFeatures.MONUMENT,
          ]),
          interiorQuality(),
          buildingTypeField(
            'apartment',
            [
              BuildingTypeOption.APARTMENT,
              BuildingTypeOption.ATTIC,
              BuildingTypeOption.BASEMENT,
              BuildingTypeOption.DUPLEX,
              BuildingTypeOption.ENTRESOL,
              BuildingTypeOption.GROUND_FLOOR,
              BuildingTypeOption.LOFT,
              BuildingTypeOption.PENTHOUSE,
              BuildingTypeOption.TERRACE,
              BuildingTypeOption.OTHER,
            ],
            getDefaultClasses('full')
          ),
          bathroomField(getDefaultClasses('left')),
          noBedrooms('right'),
          moveInDate(getDefaultClasses('left')),
          rentSubsidy(getDefaultClasses('right')),
          floorAndTotalFloor('left'),
          usableAreaField(undefined, getDefaultClasses('rightOnDesk')),
          rentalIncome(),
          parkingSpace({
            showParkingSpaceType: true,
            hasGarageLabel: true,
            parkingSpaceRentTypeLabel: 'RentBuyingPrice',
          }),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote={true} hasLocationNote={true} hasOtherNote={true} />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
        allowMultimediaLinks
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_CONSTRUCTION],
    element: (props) => <Construction {...props} hasLastModernizationField={true} />,
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager />,
  },
]

export const agentApartmentSellFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.apartment',
    offerType: 'wizard.navigation.header.sells',
    path: AGENT_APARTMENT_SELL_FLOW_PATH,
    name: FlowName.AGENT_APARTMENT_SELL,
    validationSchema: agentApartmentSellSchema,
    priceToApiMapper: priceToApiSell,
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
    options: {
      hasTextSuggestions: true,
    },
    metaData: {
      category: RealEstateCategory.APARTMENT,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.SELL,
    },
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    rented: false,
  },
  flowScreens: agentApartmentSellFlowScreens,
}
